import {
  ExpandableSection,
  Spinner,
  Tabs,
  Toggle,
  Button,
  Alert,
  Grid,
  Box,
} from '@amzn/awsui-components-core';
import arraySort from 'array-sort';
import {
  ContentAccessUser,
  ContentHistory,
  ContentWorkflow,
  UserCommentState,
  UserThread,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useCallback, useEffect, useState, type JSX } from 'react';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import constants from '../../common/constants';
import CommentHistory from './CommentHistory';
import SendCommentForm from './SendCommentForm';
import ThreadBox from './ThreadBox';

interface Props {
  contentId: string;
  workflowId: number;
  sectionId: string;
  commentThreads?: UserThread[];
  visible?: boolean;
  workflow?: ContentWorkflow;
  workflowHistory?: ContentHistory[];
  authorId?: string;
  coAuthors?: ContentAccessUser[];
  resolveEnabled?: boolean;
  showHistory?: boolean;
  isEditable?: boolean;
}

const mplServiceClient = MplServiceClientFactory.create();

// Module-level so the defaults keep a stable identity across renders, as defaultProps did.
const NO_WORKFLOW = new ContentWorkflow();
const NO_WORKFLOW_HISTORY: ContentHistory[] = [];
const NO_CO_AUTHORS: ContentAccessUser[] = [];

const CommentThreadsView = (props: Props) => {
  const {
    contentId,
    workflowId,
    sectionId,
    visible = false,
    workflow = NO_WORKFLOW,
    workflowHistory = NO_WORKFLOW_HISTORY,
    authorId,
    coAuthors = NO_CO_AUTHORS,
    showHistory = true,
    resolveEnabled = false,
    isEditable = true,
  } = props;
  const activeWorkflowId =
    workflow && workflow.draftState === WorkflowState.UNMODIFIED
      ? workflow.publicationWorkflowId
      : workflowId;
  const [threads, setThreads] = useState<UserThread[]>([]);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [showResolved, setShowResolved] = useState(resolveEnabled ?? false);

  // State to handle fail to load/reload section comments.
  const [isReloading, setIsReloading] = useState(false);
  const [loadThreadsFailed, setLoadThreadsFailed] = useState(false);

  const reloadThreads = useCallback(async () => {
    try {
      setIsReloading(true);
      const { commentThreads } = await mplServiceClient.listCommentThreads(
        contentId,
        activeWorkflowId,
        sectionId,
      );
      setThreads(commentThreads);
      setLoadThreadsFailed(false);
    } catch (e) {
      setLoadThreadsFailed(true);
    } finally {
      setIsReloading(false);
    }
  }, [contentId, activeWorkflowId, sectionId]);

  useEffect(() => {
    const fetchThreads = async () => {
      setIsLoadingThreads(true);
      await reloadThreads();
      setIsLoadingThreads(false);
    };
    if (visible) {
      fetchThreads();
    }
  }, [props.sectionId, props.workflowId, reloadThreads, visible]);

  const submitNewThread = async (commentText: string) => {
    await mplServiceClient.createCommentThread(contentId, activeWorkflowId, sectionId, commentText);
    await reloadThreads();
  };

  if (!visible) {
    return null;
  }

  if (loadThreadsFailed) {
    return isReloading ? (
      <Spinner />
    ) : (
      <Alert type="warning" action={<Button onClick={reloadThreads}>Reload comments</Button>}>
        Unable to load comments for section. Please try to reload.
      </Alert>
    );
  }

  let sortedThreads = arraySort(threads, 'createdAt');
  sortedThreads = showResolved
    ? sortedThreads
    : sortedThreads.filter((thread) => thread.state !== UserCommentState.RESOLVED);
  const numThreads = sortedThreads.length;

  const threadList = sortedThreads.map((thread, idx) => {
    const variant = idx % 2 ? 'right' : 'left';
    return (
      <ThreadBox
        key={thread.commentId}
        thread={thread}
        variant={variant}
        contentAuthorId={authorId || (workflow && workflow.ownerId)}
        contentCoAuthorIds={coAuthors?.map((ca) => ca.userId)}
        onThreadResolved={reloadThreads}
        onThreadDeleted={reloadThreads}
        onThreadUpdated={reloadThreads}
        isEditable={isEditable}
      />
    );
  });

  const activeThreadTab = (
    <div className={isEditable ? 'comment-section-active' : 'comment-section-disabled'}>
      <div className="comment-show-resolved-button">
        <Grid gridDefinition={[{ colspan: 3, offset: 9 }]}>
          <Toggle checked={showResolved} onChange={({ detail }) => setShowResolved(detail.checked)}>
            Show resolved
          </Toggle>
        </Grid>
      </div>
      <Box variant="div" padding="l">
        {threadList}
        <SendCommentForm
          onComment={submitNewThread}
          formPlaceholder={constants.COMMENTS_FORM_PLACEHOLDER}
          buttonText={constants.COMMENTS_FORM_BUTTON_TEXT}
          buttonVariant="primary"
          visible={isEditable}
        />
      </Box>
    </div>
  );

  let threadBodySection: JSX.Element | null = null;

  if (
    workflow?.draftWorkflowId === 1 ||
    !showHistory ||
    !workflowHistory ||
    !workflowHistory.length
  ) {
    threadBodySection = activeThreadTab;
  } else {
    const commentTabs = [
      {
        content: activeThreadTab,
        id: 'activeThread',
        label: constants.COMMENTS_ACTIVE_THREAD_TAB,
      },
      {
        content: (
          <CommentHistory
            contentId={contentId}
            workflow={workflow}
            workflowHistory={workflowHistory}
            sectionId={sectionId}
          />
        ),
        id: 'historyThread',
        label: constants.COMMENTS_HISTORY_TAB,
      },
    ];
    threadBodySection = <Tabs tabs={commentTabs} ariaLabel="Comment thread navigation" />;
  }

  if (isLoadingThreads) {
    return <Spinner />;
  }

  if (!numThreads && !isEditable) {
    return <p>No comment</p>;
  }

  const commentThreadView = isEditable ? (
    <Box padding={{ top: 's' }}>
      <ExpandableSection
        headerText={numThreads > 0 ? `Comments (${numThreads})` : 'Post a comment'}
      >
        {threadBodySection}
      </ExpandableSection>
    </Box>
  ) : (
    threadBodySection
  );

  return (
    <Box variant="div" padding={{ bottom: 'l' }}>
      {commentThreadView}
    </Box>
  );
};

export default CommentThreadsView;
